// src/pages/LicensePage.js
import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Avatar,
  useTheme,
  Divider
} from '@mui/material';
import {
  Description as DescriptionIcon,
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

export default function LicensePage() {
  const theme = useTheme();
  const [licenseText, setLicenseText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Завантажуємо текст ліцензії з файлу /public/license.txt
    fetch('/license.txt')
      .then(response => {
        if (!response.ok) {
          throw new Error('No se pudo cargar el archivo de licencia');
        }
        return response.text();
      })
      .then(text => {
        setLicenseText(text);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching license:', error);
        setError('Error al cargar el acuerdo de licencia');
        setLoading(false);
      });
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.light} 100%)`,
        p: 3
      }}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Заголовок */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: theme.palette.primary.main,
              mx: 'auto',
              mb: 2
            }}
          >
            <SecurityIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
            Acuerdo de Licencia
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Términos y condiciones de uso del software Lainsa
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <DescriptionIcon color="primary" />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Licencia de Software
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={60} />
              </Box>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.default,
                  maxHeight: '60vh',
                  overflowY: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  lineHeight: 1.6
                }}
              >
                <pre style={{ 
                  margin: 0, 
                  whiteSpace: 'pre-wrap', 
                  wordWrap: 'break-word',
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  lineHeight: 'inherit'
                }}>
                  {licenseText || 'No se pudo cargar el texto de la licencia.'}
                </pre>
              </Paper>
            )}

            <Alert 
              severity="info" 
              sx={{ 
                mt: 3, 
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <CheckCircleIcon color="info" sx={{ mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Información Importante
                  </Typography>
                  <Typography variant="body2">
                    Al utilizar este software, usted acepta cumplir con los términos y condiciones 
                    establecidos en este acuerdo de licencia. Si no está de acuerdo con estos términos, 
                    no utilice el software.
                  </Typography>
                </Box>
              </Box>
            </Alert>
          </CardContent>
        </Card>

        {/* Інформаційні картки */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          <Card sx={{ flex: 1, minWidth: 250, borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Avatar sx={{ bgcolor: theme.palette.success.light, width: 60, height: 60, mx: 'auto', mb: 2 }}>
                <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 30 }} />
              </Avatar>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Uso Autorizado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Este software está autorizado para uso interno en operaciones de lavandería.
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1, minWidth: 250, borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 60, height: 60, mx: 'auto', mb: 2 }}>
                <SecurityIcon sx={{ color: theme.palette.warning.main, fontSize: 30 }} />
              </Avatar>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Confidencialidad
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Toda la información del sistema es confidencial y está protegida.
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Кнопка повернення */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to="/"
              variant="contained"
              size="large"
              startIcon={<ArrowBackIcon />}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600
              }}
            >
              Volver al Inicio de Sesión
            </Button>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="caption" color="text.secondary">
              Lainsa v1.0.0 • Sistema de Gestión de Lavandería • {new Date().getFullYear()}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}


